<template>
	<BaseModal :title="t('title')" v-model="model">
		<div class="px-5">
			<TagGroup
				class="mt-2 *:flex-1"
				v-model="layout.fairInfoTab"
				:tags="tabs"
			/>
			<BaseCard class="mt-4 flex w-full gap-4 p-5" alt>
				<div
					class="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#3F3F3F]"
				>
					<GlassesIcon class="h-6 w-6 text-[#FFD326]" />
				</div>
				<div>
					<div class="text-label-primary text-sm font-medium uppercase">
						{{ t('tldr') }}
					</div>
					<div class="text-label-secondary mt-1 text-sm">
						{{ tldrText }}
					</div>
				</div>
			</BaseCard>
			<BaseButton
				class="mt-4 w-full"
				size="lg"
				color="primary"
				:loading="opening"
				@click="openVerification"
			>
				{{ t('cta') }}
			</BaseButton>
			<div class="bg-fill-secondary mt-5 h-px w-full" />
			<SubtitleText class="mt-5 uppercase!">
				{{ t('howGenerate') }}
			</SubtitleText>
			<div class="mt-4 space-y-4" v-if="layout.fairInfoTab === 0">
				<FairText :text="t('battles.text1')" />
				<FairText :text="t('battles.text2')" active />
				<FairText :text="t('battles.text3')" />
			</div>
			<div class="mt-4 space-y-4" v-if="layout.fairInfoTab === 1">
				<FairText :text="t('cases.text1')" />
				<FairText :text="t('cases.text2')" active />
			</div>
			<div class="bg-fill-secondary mt-5 h-px w-full" />
			<SubtitleText class="mt-5 uppercase!">
				{{ t('howFairness') }}
			</SubtitleText>
			<FairText
				:text="layout.fairInfoTab === 0 ? t('battles.text4') : t('cases.text3')"
				class="mt-4"
			/>
		</div>
	</BaseModal>
</template>
<script setup lang="ts">
import { computed, ref } from 'vue'
import BaseModal from './BaseModal.vue'
import BaseButton from '../BaseButton.vue'
import BaseCard from '../BaseCard.vue'
import { useI18n } from 'vue-i18n'
import TagGroup from '../TagGroup.vue'
import BattlesIcon from '@/assets/icons/battles.svg'
import CaseIcon from '@/assets/icons/case.svg'
import { useLayout } from '@/stores/layout'
import { WebApp } from '@grammyjs/web-app'
import { sleep } from '@/utils/sleep'
import GlassesIcon from '@/assets/icons/glasses.svg'
import SubtitleText from '../SubtitleText.vue'
import FairText from '../FairText.vue'

const { t } = useI18n({
	messages: {
		en: {
			title: 'fairness',
			tldr: 'TLDR:',
			cta: 'open verification sandbox',
			howGenerate: 'how we generate the outcome',
			howFairness: 'how to verify game fairness',
			battles: {
				tab: 'Battles',
				tldr: 'Fairness is ensured by public TON blockchain data.',
				text1:
					'When a new battle is created, our server generates a random seed. The hash of that seed is shared publicly and serves as a fingerprint. Once the battle is full, we lock in the next TON block. Its hash, when revealed, will be used as a public seed.',
				text2:
					'It is impossible for anyone, including us, to know the hash of this block in advance, so it serves as a publicly verifiable way to generate truly random numbers.',
				text3:
					'Once the block is mined and its hash is revealed, we combine our server seed with this public blockchain data, the round number, and the user’s battle position to generate the final ticket number for each case. The same goes for tie-breaking situation and jackpot gamemode. This process ensures the randomness and fairness of our battles.',
				text4:
					'After the game is complete, open the fairness modal on the battle page and copy the battle data JSON. Then paste it into our sandbox linked above to verify fairness.'
			},
			cases: {
				tab: 'Cases',
				tldr: 'Fairness is ensured by a server-generated and chosen client seed.',
				text1:
					'To generate the outcome item ticket, we combine server-generated seed with a client-chosen seed and nonce. This system guarantees that every pull is transparent and fair, allowing you to influence the results with your selected seed.',
				text2:
					'The publicly available hashed server seed acts as a fingerprint for the server-generated seed, ensuring that the random ticket generation cannot be manipulated.',
				text3:
					'Rotate the server seed in your profile to display the seed used in your previous unboxings. Then, go to the game transaction and copy the unboxing data JSON. Finally, paste it into our sandbox linked above to verify fairness.'
			}
		},
		ru: {
			title: 'честность',
			tldr: 'В кратце:',
			cta: 'открыть страницу проверки',
			howGenerate: 'как мы генерируем результат',
			howFairness: 'как проверить честность игры',
			battles: {
				tab: 'Батлы',
				tldr: 'Честность обеспечивается публичными данными блокчейна TON.',
				text1:
					'Когда создается новый батл, наш сервер генерирует случайный сид. Хеш этого сида публикуется и служит отпечатком. Как только все игроки вступили в батл, мы фиксируем следующий TON блок. Когда станет известен его хеш, он будет использован как публичный сид.',
				text2:
					'Никто, включая нас, не может заранее знать хеш этого блока, поэтому он служит публично проверяемым способом генерации действительно случайных чисел.',
				text3:
					'После того, как блок смайнят и его хеш станет известен, мы, используя наш серверный сид с публично известным хешем блока, номер раунда и номер слота пользователя в батле, генерируем окончательный номера билета для каждого кейса. Точно также работает тай-брейк (определение победителя в случае ничьей) и режим Jackpot. Этот процесс обеспечивает случайность и честность наших батлов.',
				text4:
					'После завершения игры откройте окно проверки честности на странице батла и скопируйте JSON-данные батла. Затем вставьте их в нашу песочницу по ссылке выше, чтобы проверить честность.'
			},
			cases: {
				tab: 'Кейсы',
				tldr: 'Честность обеспечивается серверным и клиентским сидами.',
				text1:
					'Для генерации билета выигрышного предмета мы используем серверный сид с клиентским сидом и нонсом. Эта система гарантирует, что каждый спин прозрачен и честен, позволяя вам влиять на результаты с помощью выбранного вами клиентского сида.',
				text2:
					'Публично доступный хеш серверного сид служит его отпечатком. Наличие отпечатка гарантирует, что серверный сид не меняется во время игры. Это исключает возможность манипулирования генерацией случайных билетов.',
				text3:
					'Смените серверный сид в своем профиле, чтобы отобразить старый сид, использованный в ваших предыдущих открытиях. Затем перейдите к транзакции игры и скопируйте JSON-данные открытия. Наконец, вставьте их в нашу песочницу по ссылке выше, чтобы проверить честность.'
			}
		}
	}
})

const layout = useLayout()

const model = defineModel<boolean>()

const tabs = computed(() => [
	{
		icon: BattlesIcon,
		text: t('battles.tab')
	},
	{
		icon: CaseIcon,
		text: t('cases.tab')
	}
])

const opening = ref(false)

const openVerification = async () => {
	opening.value = true

	WebApp.openLink('https://codepen.io/ragegift/full/VYjwWwx')

	await sleep(1500)

	opening.value = false
}

const tldrText = computed(() => {
	if (layout.fairInfoTab === 0) {
		return t('battles.tldr')
	} else {
		return t('cases.tldr')
	}
})
</script>
